/**
 * testMappingHelp.tsx — page-guide definition for Admin → Test Mapping.
 *
 * DATA file (CODING-STANDARDS column-config pattern): the structure is a
 * plain HelpContent object; JSX appears only for the live example chips,
 * which reuse the page's REAL components so users see exactly what the page
 * shows. Rendered by PageHelpDialog; shape-checked by
 * src/__tests__/pageHelpContent.test.ts.
 */

import { Chip, alpha, useTheme } from '@mui/material';
import { TestStatusChip } from '../../../pages/admin/testMapping/TestListColumn';
import { ROUTES } from '../../../constants/routes';
import type { HelpContent } from '../helpTypes';

/**
 * The blue work-order-line count chip, styled exactly like the list rows
 * (same theme token + geometry as TestListColumn).
 */
const WoLinesChipExample = () => {
  const theme = useTheme();
  return (
    <Chip
      size="small"
      label="12 lines"
      sx={{
        height: 18,
        fontSize: '0.6rem',
        fontWeight: 600,
        backgroundColor: alpha(theme.schedule.machineTime, 0.1),
        color: theme.schedule.machineTime,
      }}
    />
  );
};

/** All four status chips, rendered by the page's own component. */
const statusChipsExample = (
  <>
    <TestStatusChip status="unmapped" />
    <TestStatusChip status="unconfirmed" />
    <TestStatusChip status="confirmed" />
    <TestStatusChip status="nameMatch" />
    <WoLinesChipExample />
  </>
);

export const testMappingHelp: HelpContent = {
  pageKey: 'test-mapping',
  title: 'Test Mapping — page guide',
  heroSrc: '/help/img/mapping-hero.webp',
  heroAlt: 'Illustration of lab tests being connected to catalogue tests',
  overview: [
    'Lab tests and catalogue tests are two different lists. Engineers pick a lab test on a '
    + 'work order; the schedule and quotes work from the catalogue. This page connects each '
    + 'lab test to its catalogue test — one to one.',
    'That link is what turns work-order lines into schedule tasks. When it is missing, '
    + 'nothing breaks loudly: the lines just never appear in the schedule, and Scenario '
    + 'Planning shows them as “not schedulable”.',
    'Pick a lab test on the left and connect it on the right. Unmapped tests sort to the '
    + 'top, so the most urgent work is always in view.',
  ],
  snapshot: {
    intro: 'This sketch mirrors the page. Click a numbered dot (or an entry on the right) '
      + 'to see what that part does.',
    areas: [
      { id: 'header', sketch: 'titleBar', rect: { left: 1.5, top: 2, width: 97, height: 9 }, marker: { left: 4.5, top: 6.5 } },
      { id: 'list', sketch: 'listColumn', rect: { left: 1.5, top: 13.5, width: 32, height: 84.5 }, marker: { left: 4.5, top: 18.5 } },
      { id: 'picker', sketch: 'pickerPane', rect: { left: 35.5, top: 13.5, width: 63, height: 27 }, marker: { left: 38.5, top: 18.5 } },
      { id: 'controls', sketch: 'chipRow', rect: { left: 35.5, top: 42.5, width: 63, height: 20 }, marker: { left: 38.5, top: 47.5 } },
      { id: 'chain', sketch: 'chainRow', rect: { left: 35.5, top: 64.5, width: 63, height: 33.5 }, marker: { left: 38.5, top: 69.5 } },
    ],
    items: [
      {
        id: 'header',
        title: 'Progress counts',
        points: [
          '“X of Y tests mapped” tracks overall progress.',
          'The amber chips are shortcuts: click “unconfirmed” or “work-order lines affected” '
          + 'to filter the list straight to those tests.',
        ],
      },
      {
        id: 'list',
        title: 'The lab-test list',
        points: [
          'Search, plus All / Unmapped / Unconfirmed / Confirmed filter chips (Confirmed '
          + 'includes name matches).',
          'Unmapped tests sort first, then unconfirmed — problems always float to the top.',
          'The blue “N lines” chip is the test’s work-order line count: how much work is '
          + 'riding on this mapping.',
          'Unmapped (amber) — no catalogue test connected; not schedulable.',
          'Unconfirmed (amber outline) — mapped from an estimate. It schedules; confirm it '
          + 'once the lab verifies.',
          'Confirmed (sage) — the lab has blessed this mapping.',
          'Name match (grey) — no explicit mapping, but a catalogue test with the identical '
          + 'name resolves it. Pin it to make the link explicit and rename-proof.',
        ],
        example: statusChipsExample,
        exampleCaption: 'The chips exactly as they appear on a row:',
      },
      {
        id: 'picker',
        title: 'Catalogue picker and suggestions',
        points: [
          'Type to search every catalogue test, grouped by lab section, and pick one to map.',
          'Suggested matches lists close name matches with a similarity score — Accept one '
          + 'in a single click.',
          'New and accepted mappings start as Unconfirmed until the lab blesses them.',
          'Replacing an existing mapping always asks first.',
        ],
      },
      {
        id: 'controls',
        title: 'Confirm, notes, unmap',
        points: [
          'Confirmed switch — flip it on once the lab verifies the mapping.',
          'Notes — record why a mapping was chosen (helpful for odd name pairs).',
          'Unmap warns how many work-order lines will stop generating schedule tasks '
          + 'before it does anything.',
        ],
      },
      {
        id: 'chain',
        title: 'Chain explorer',
        points: [
          'Previews what the mapped catalogue test will actually schedule: its task types, '
          + 'machine time, and required skill.',
          'Jumps straight to the Test Catalogue if those details need editing.',
        ],
      },
    ],
  },
  connections: {
    imageSrc: '/help/img/connections-hero.webp',
    intro: 'This page owns the middle link of the chain. Everything to the right of it — '
      + 'machine time, skills, prices, the planner — only works for a lab test once its '
      + 'mapping exists. Click any chip with an arrow icon to go there.',
    stages: [
      {
        title: 'The lab side',
        nodes: [
          { id: 'labTest', label: 'Lab test', sublabel: 'what engineers pick on a work order', route: ROUTES.TESTS, tone: 'neutral' },
        ],
      },
      {
        title: 'This page',
        nodes: [
          { id: 'mapping', label: 'Test Mapping', sublabel: 'connects each lab test to one catalogue test', route: ROUTES.ADMIN_TEST_MAPPING, tone: 'sage' },
        ],
      },
      {
        title: 'The definition',
        nodes: [
          { id: 'catalog', label: 'Catalogue test', sublabel: 'one object: price + scheduling', route: ROUTES.ADMIN_TEST_CATALOG, tone: 'sage' },
        ],
      },
      {
        title: 'What it carries',
        nodes: [
          { id: 'machineTime', label: 'Machine time & run mode', sublabel: 'minutes; attended or 24/7', tone: 'machine' },
          { id: 'machines', label: 'Capable machines', sublabel: 'which machines can run it', route: ROUTES.SCHEDULE_MACHINES, tone: 'machine' },
          { id: 'skill', label: 'Required skill', sublabel: 'who is qualified to run it', route: ROUTES.SCHEDULE_SKILL_MATRIX, tone: 'skill' },
          { id: 'price', label: 'Price & assumptions', sublabel: 'the commercial side', tone: 'quote' },
        ],
      },
      {
        title: 'Who uses it',
        nodes: [
          { id: 'scenario', label: 'Scenario Planner', sublabel: 'plans machines and people', route: ROUTES.SCHEDULE_SCENARIO, tone: 'sage' },
          { id: 'quotes', label: 'Quotes', sublabel: 'prices the work', route: ROUTES.QUOTES, tone: 'quote' },
        ],
      },
    ],
    footnote: 'An unmapped lab test does not fail loudly — its work-order lines just never '
      + 'become schedule tasks. The orange “N not schedulable” chip in Scenario Planning '
      + 'counts exactly these.',
  },
};
